import { motion, AnimatePresence } from 'framer-motion'
import { Zap, Cpu, Database, Network, Shield, Activity } from 'lucide-react'

interface ReloadExperienceProps {
  isActive: boolean
  onComplete?: () => void
}

export function ReloadExperience({ isActive, onComplete }: ReloadExperienceProps) {
  const systemStats = [
    { icon: Cpu, label: 'Processing', value: '99.9%', color: 'from-blue-500 to-cyan-500' },
    { icon: Database, label: 'Cache Clear', value: 'Complete', color: 'from-purple-500 to-pink-500' },
    { icon: Network, label: 'Network', value: 'Optimized', color: 'from-green-500 to-emerald-500' },
    { icon: Shield, label: 'Security', value: 'Verified', color: 'from-orange-500 to-red-500' },
    { icon: Activity, label: 'Systems', value: 'Online', color: 'from-yellow-500 to-orange-500' },
  ]

  return (
    <AnimatePresence mode="wait">
      {isActive && (
        <>
          {/* Persistent full-screen overlay - prevents any flash */}
          <div className="fixed inset-0 z-[9998] bg-white dark:bg-gray-950" />
          
          {/* Animated reload experience */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 1 }}
            transition={{ duration: 0.3 }}
            className="fixed inset-0 z-[9999] flex items-center justify-center overflow-hidden bg-gradient-to-br from-gray-50 via-white to-gray-100 dark:from-gray-950 dark:via-gray-900 dark:to-gray-950"
          >
          {/* Animated Grid Background */}
          <div className="absolute inset-0 opacity-10 dark:opacity-20">
            <motion.div
              animate={{
                backgroundPosition: ['0% 0%', '100% 100%'],
              }}
              transition={{
                duration: 20,
                repeat: Infinity,
                ease: 'linear',
              }}
              className="w-full h-full bg-grid-light dark:bg-grid-dark"
              style={{
                backgroundImage: `
                  linear-gradient(rgba(59, 130, 246, 0.5) 1px, transparent 1px),
                  linear-gradient(90deg, rgba(59, 130, 246, 0.5) 1px, transparent 1px)
                `,
                backgroundSize: '50px 50px',
              }}
            />
          </div>

          {/* Swirling Particle Vortex */}
          {[...Array(30)].map((_, i) => (
            <motion.div
              key={`particle-${i}`}
              className="absolute w-2 h-2 rounded-full"
              style={{
                background: `radial-gradient(circle, ${
                  i % 3 === 0 ? '#3b82f6' : i % 3 === 1 ? '#8b5cf6' : '#ec4899'
                }, transparent)`,
                filter: 'blur(1px)',
              }}
              animate={{
                x: [
                  Math.cos((i / 30) * Math.PI * 2) * 200,
                  Math.cos((i / 30) * Math.PI * 2) * 400,
                  Math.cos((i / 30) * Math.PI * 2) * 200,
                ],
                y: [
                  Math.sin((i / 30) * Math.PI * 2) * 200,
                  Math.sin((i / 30) * Math.PI * 2) * 400,
                  Math.sin((i / 30) * Math.PI * 2) * 200,
                ],
                scale: [1, 2, 1],
                opacity: [0.3, 0.8, 0.3],
              }}
              transition={{
                duration: 3,
                repeat: Infinity,
                delay: i * 0.1,
                ease: 'easeInOut',
              }}
            />
          ))}

          {/* Electric Ring Pulses */}
          {[...Array(5)].map((_, i) => (
            <motion.div
              key={`ring-${i}`}
              className="absolute rounded-full border-2"
              style={{
                borderColor: i % 2 === 0 ? '#3b82f6' : '#8b5cf6',
                width: '100px',
                height: '100px',
              }}
              animate={{
                scale: [1, 8],
                opacity: [1, 0],
                rotate: [0, 360],
              }}
              transition={{
                duration: 3,
                repeat: Infinity,
                delay: i * 0.6,
                ease: 'easeOut',
              }}
            />
          ))}

          {/* Central 3D Rotating Core */}
          <motion.div
            className="relative z-10"
            animate={{
              rotateY: [0, 360],
            }}
            transition={{
              duration: 4,
              repeat: Infinity,
              ease: 'linear',
            }}
            style={{
              transformStyle: 'preserve-3d',
              perspective: '1000px',
            }}
          >
            {/* Hexagon Core */}
            <motion.div
              className="relative w-48 h-48"
              animate={{
                rotate: [0, 360],
              }}
              transition={{
                duration: 6,
                repeat: Infinity,
                ease: 'linear',
              }}
            >
              {/* Hexagon Shape */}
              <svg viewBox="0 0 100 100" className="w-full h-full">
                <defs>
                  <linearGradient id="hexGradient" x1="0%" y1="0%" x2="100%" y2="100%">
                    <stop offset="0%" stopColor="#3b82f6" />
                    <stop offset="50%" stopColor="#8b5cf6" />
                    <stop offset="100%" stopColor="#ec4899" />
                  </linearGradient>
                  <filter id="glow">
                    <feGaussianBlur stdDeviation="4" result="coloredBlur"/>
                    <feMerge>
                      <feMergeNode in="coloredBlur"/>
                      <feMergeNode in="SourceGraphic"/>
                    </feMerge>
                  </filter>
                </defs>
                <motion.polygon
                  points="50,5 95,27.5 95,72.5 50,95 5,72.5 5,27.5"
                  fill="none"
                  stroke="url(#hexGradient)"
                  strokeWidth="3"
                  filter="url(#glow)"
                  animate={{
                    strokeDashoffset: [0, -500],
                  }}
                  transition={{
                    duration: 2,
                    repeat: Infinity,
                    ease: 'linear',
                  }}
                  strokeDasharray="50 50"
                />
              </svg>

              {/* Center Zap Icon */}
              <motion.div
                className="absolute inset-0 flex items-center justify-center"
                animate={{
                  scale: [1, 1.2, 1],
                  rotate: [0, 180, 360],
                }}
                transition={{
                  duration: 2,
                  repeat: Infinity,
                  ease: 'easeInOut',
                }}
              >
                <Zap className="w-16 h-16 text-blue-600 dark:text-blue-400" fill="currentColor" />
              </motion.div>

              {/* Energy Orbs */}
              {[...Array(6)].map((_, i) => (
                <motion.div
                  key={`orb-${i}`}
                  className="absolute w-3 h-3 rounded-full"
                  style={{
                    background: `radial-gradient(circle, ${
                      i % 3 === 0 ? '#3b82f6' : i % 3 === 1 ? '#8b5cf6' : '#ec4899'
                    }, transparent)`,
                    top: '50%',
                    left: '50%',
                    boxShadow: `0 0 20px ${
                      i % 3 === 0 ? '#3b82f6' : i % 3 === 1 ? '#8b5cf6' : '#ec4899'
                    }`,
                  }}
                  animate={{
                    x: [
                      0,
                      Math.cos((i / 6) * Math.PI * 2) * 100,
                      0,
                    ],
                    y: [
                      0,
                      Math.sin((i / 6) * Math.PI * 2) * 100,
                      0,
                    ],
                    scale: [1, 1.5, 1],
                  }}
                  transition={{
                    duration: 2,
                    repeat: Infinity,
                    delay: i * 0.2,
                    ease: 'easeInOut',
                  }}
                />
              ))}
            </motion.div>
          </motion.div>

          {/* Holographic Scanning Lines */}
          <motion.div
            className="absolute inset-0"
            animate={{
              y: ['-100%', '100%'],
            }}
            transition={{
              duration: 2,
              repeat: Infinity,
              ease: 'linear',
            }}
          >
            <div className="h-32 w-full bg-gradient-to-b from-transparent via-blue-500/20 to-transparent" />
          </motion.div>

          {/* System Status Cards */}
          <div className="absolute bottom-20 left-1/2 -translate-x-1/2 flex gap-6 z-20">
            {systemStats.map((stat, i) => {
              const Icon = stat.icon
              return (
                <motion.div
                  key={stat.label}
                  initial={{ opacity: 0, y: 50, scale: 0.5 }}
                  animate={{ opacity: 1, y: 0, scale: 1 }}
                  transition={{
                    delay: i * 0.1,
                    duration: 0.5,
                    type: 'spring',
                  }}
                  className="relative"
                >
                  <div className="bg-white/60 dark:bg-black/40 backdrop-blur-xl border border-gray-300/30 dark:border-white/10 rounded-xl p-4 min-w-[140px] shadow-lg">
                    <motion.div
                      className="flex items-center gap-2 mb-2"
                      animate={{
                        opacity: [0.5, 1, 0.5],
                      }}
                      transition={{
                        duration: 2,
                        repeat: Infinity,
                        delay: i * 0.2,
                      }}
                    >
                      <Icon className={`w-5 h-5 bg-gradient-to-r ${stat.color} bg-clip-text text-transparent`} />
                      <span className="text-xs text-gray-600 dark:text-gray-400 font-medium">{stat.label}</span>
                    </motion.div>
                    <motion.div
                      className={`text-sm font-bold bg-gradient-to-r ${stat.color} bg-clip-text text-transparent`}
                      animate={{
                        scale: [1, 1.05, 1],
                      }}
                      transition={{
                        duration: 1,
                        repeat: Infinity,
                        delay: i * 0.15,
                      }}
                    >
                      {stat.value}
                    </motion.div>
                  </div>
                  
                  {/* Glowing border effect */}
                  <motion.div
                    className="absolute inset-0 rounded-xl"
                    style={{
                      background: `linear-gradient(135deg, transparent, ${
                        i % 3 === 0 ? '#3b82f6' : i % 3 === 1 ? '#8b5cf6' : '#ec4899'
                      }30, transparent)`,
                    }}
                    animate={{
                      opacity: [0, 0.5, 0],
                    }}
                    transition={{
                      duration: 2,
                      repeat: Infinity,
                      delay: i * 0.3,
                    }}
                  />
                </motion.div>
              )
            })}
          </div>

          {/* Loading Text */}
          <motion.div
            className="absolute top-1/3 left-1/2 -translate-x-1/2 z-20"
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
          >
            <motion.h2
              className="text-4xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-blue-600 via-purple-600 to-pink-600 dark:from-blue-400 dark:via-purple-400 dark:to-pink-400 mb-4 text-center drop-shadow-lg"
              animate={{
                backgroundPosition: ['0% 50%', '100% 50%', '0% 50%'],
              }}
              transition={{
                duration: 3,
                repeat: Infinity,
                ease: 'linear',
              }}
              style={{
                backgroundSize: '200% auto',
              }}
            >
              INITIALIZING SYSTEMS
            </motion.h2>
            
            {/* Animated dots */}
            <div className="flex justify-center gap-2">
              {[...Array(3)].map((_, i) => (
                <motion.div
                  key={i}
                  className="w-3 h-3 rounded-full bg-blue-600 dark:bg-blue-400 shadow-lg"
                  animate={{
                    scale: [1, 1.5, 1],
                    opacity: [0.3, 1, 0.3],
                  }}
                  transition={{
                    duration: 1.5,
                    repeat: Infinity,
                    delay: i * 0.3,
                  }}
                />
              ))}
            </div>
          </motion.div>

          {/* Corner Accent Lines */}
          {['top-left', 'top-right', 'bottom-left', 'bottom-right'].map((corner) => (
            <motion.div
              key={corner}
              className="absolute"
              style={{
                ...(corner.includes('top') ? { top: 20 } : { bottom: 20 }),
                ...(corner.includes('left') ? { left: 20 } : { right: 20 }),
              }}
              initial={{ opacity: 0, scale: 0 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: 0.3, duration: 0.5 }}
            >
              <svg width="60" height="60" viewBox="0 0 60 60">
                <motion.path
                  d={
                    corner === 'top-left' ? 'M60,0 L0,0 L0,60' :
                    corner === 'top-right' ? 'M0,0 L60,0 L60,60' :
                    corner === 'bottom-left' ? 'M60,60 L0,60 L0,0' :
                    'M0,60 L60,60 L60,0'
                  }
                  stroke="url(#hexGradient)"
                  strokeWidth="3"
                  fill="none"
                  initial={{ pathLength: 0 }}
                  animate={{ pathLength: 1 }}
                  transition={{ duration: 1, delay: 0.5 }}
                />
              </svg>
            </motion.div>
          ))}
        </motion.div>
        </>
      )}
    </AnimatePresence>
  )
}

